import { useEffect, useMemo, useRef } from 'react'
import { useAppSelector } from '../../redux/hooks'
import { selectAuth } from '../../redux/reducers/authSlice'
import { initializeSmartlook, isSmartlookEnabledForCompany } from './smartlook-init'
import { loadSmartlookSDK } from './smartlook-loader'

/**
 * Smartlook integration component
 *
 * Handles:
 * - Smartlook initialization (only if enabled for company)
 * - User identification when authenticated
 */
export const LCSmartlook = (): null => {
  const auth = useAppSelector(selectAuth)
  const isAuthenticated = Boolean(auth.token && auth.companyToken)
  const companyId = auth.user?.companyId
  const initializedRef = useRef<boolean>(false)

  // Prepare Smartlook identify payload
  const smartlookIdentifyPayload = useMemo(() => {
    // We keep the payload minimal and explicit because it may contain PII.
    // Smartlook also requires enabling "Identify users via API" in project settings.
    const firstName = auth.user?.firstName || ''
    const lastName = auth.user?.lastName || ''
    const fullName = `${firstName} ${lastName}`.trim()

    return {
      uid: auth.user?.identity || '',
      props: {
        // Keys are arbitrary in Smartlook. We use clear names for dashboard filtering.
        name: fullName || undefined,
        email: auth.user?.email || undefined,
        companyId: auth.user?.companyId || undefined,
      } as Record<string, unknown>,
    }
  }, [auth.user?.companyId, auth.user?.email, auth.user?.firstName, auth.user?.identity, auth.user?.lastName])

  // Initialize Smartlook if enabled for this company
  useEffect(() => {
    // Only initialize once and only if we have a company ID
    if (initializedRef.current || !companyId) return

    // Check if Smartlook should be enabled for this company
    if (!isSmartlookEnabledForCompany(companyId)) {
      return
    }

    // Load SDK and initialize
    loadSmartlookSDK()
      .then(() => {
        initializeSmartlook()
        initializedRef.current = true
      })
      .catch((error) => {
        console.warn('Failed to initialize Smartlook:', error)
      })
  }, [companyId])

  // Handle user identification
  useEffect(() => {
    // Smartlook may not exist if not initialized or blocked by extensions.
    if (!window.smartlook) return

    // Identify when we are authenticated AND have a stable uid.
    // This lets support/dev quickly locate a specific user in Smartlook.
    if (isAuthenticated && smartlookIdentifyPayload.uid) {
      window.smartlook('identify', smartlookIdentifyPayload.uid, smartlookIdentifyPayload.props)
    }
  }, [isAuthenticated, smartlookIdentifyPayload])

  return null
}
